import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Button } from '@hqo/ui-atoms';
import { ChevronDown, Clock, Lock, Minus, Plus } from 'lucide-react';

import { AddOn } from 'store/resource/types';
import { addOnMessage } from '../add-on-message';
import { addOnSubtitle } from '../add-on-subtitle';
import { selectBuildingTimeZone } from 'store/building/selectors';
import { useSelector } from 'react-redux';
import { displayUnitPriceCents } from 'store/resource/price-tiers';
import { formatCurrency } from 'utils/formatCurrency';
import { useCurrencyType } from 'hooks/use-currency-type.hook';
import { useLocale } from 'hooks/use-locale.hook';

export interface AddOnItemProps {
  addOn: AddOn;
  quantity: number;
  onIncrease: (addOn: AddOn) => void;
  onDecrease: (addOn: AddOn) => void;
  /* Drop the whole line. The fixed-quantity pill's action — a decrement would leave a remainder. */
  onRemove?: (addOn: AddOn) => void;
  isExpanded: boolean;
  onToggle: (addOnId: string) => void;
  /* This add-on's party is not the one the tenant committed to. */
  isPartyLocked?: boolean;
  /* RES-1398. Kept past its notice window because the booking already carries it. */
  isKept?: boolean;
  /* The product's range admits exactly one quantity, so the stepper becomes an Add/Remove pill. */
  isFixedQuantity?: boolean;
  /* At the product's ceiling. ⚠️ STYLING ONLY — the refusal lives in the hook's `onIncrease`. */
  isAtMax?: boolean;
  /* The quantity-range read is in flight, so the bands this product prices by are not known yet. */
  isAwaitingRanges?: boolean;
  /* The smallest quantity the tenant could order, for pricing an unselected row. */
  minQuantity?: number;
  /* RES-1359. The booking's start, which dates the "Not available for Sep 24" line. */
  bookingStart?: string;
}

/**
 * RES-1367 — one add-on, as the my-bookings dialog draws it.
 *
 * ⛔ NOT A RESTYLE OF `AddOnRow`, and deliberately a second component rather than a variant of it.
 * That one is the post-checkout sheet's, built on styled-components and RCL's `Counter`, and it stays
 * exactly as it is. The rules the two share — which control to draw, when adding is held, why removal
 * never is — are NOT duplicated: they are decided by `useBookingAddOns`, which both rows are handed
 * the answers from. What is duplicated here is markup.
 *
 * ⚠️ The stepper is built from two `Button`s rather than borrowed. RCL's `Counter` taught this flow
 * that a `disabled` prop can be cosmetic — it greys the icon while the click handler stays bound —
 * and a real `<button disabled>` cannot do that.
 */
export const AddOnItem = ({
  addOn,
  quantity,
  onIncrease,
  onDecrease,
  onRemove,
  isExpanded,
  onToggle,
  isPartyLocked = false,
  isKept = false,
  isFixedQuantity = false,
  isAtMax = false,
  isAwaitingRanges = false,
  minQuantity = 1,
  bookingStart,
}: AddOnItemProps): JSX.Element => {
  const intl = useIntl();
  const buildingCurrency = useCurrencyType();
  const locale = useLocale();
  /* The BUILDING's zone, so the day named here cannot disagree with the day the booking is on. */
  const timeZone = useSelector(selectBuildingTimeZone);

  /*
   * ⭐ Per-ITEM currency, not the building's — this step can list several vendors' products, and the
   * building currency is only the fallback for an add-on that carries none. Priced at the quantity
   * being shown rather than from the scalar `price`, so a tiered row re-prices as the stepper
   * crosses a band boundary.
   */
  const price = formatCurrency(
    displayUnitPriceCents(addOn, quantity, minQuantity),
    addOn.currency ?? buildingCurrency,
    locale,
    true,
    false,
  );

  const subtitle = addOnSubtitle(addOn, { intl, price, quantity, minQuantity });

  const hasDetails = Boolean(addOn.description || addOn.image_url);
  /*
   * Nothing can be ADDED from a locked party, nor while the range read is in flight — and the control
   * must say so rather than accept the press and discard it. Removing stays available throughout, so
   * a line can never be stranded.
   */
  /* RES-1359. Read off the payload, not recomputed: helix decides it and the submit path agrees. */
  const isUnavailable = Boolean(addOn.unavailable_reason);
  const notice = addOnMessage(addOn, { intl, locale, bookingStart, timeZone, isKept });

  const canAdd = !isPartyLocked && !isAwaitingRanges && !isUnavailable;

  const handleToggle = useCallback(() => onToggle(addOn.id), [onToggle, addOn.id]);
  const handleIncrease = useCallback(() => onIncrease(addOn), [onIncrease, addOn]);
  const handleDecrease = useCallback(() => onDecrease(addOn), [onDecrease, addOn]);
  /* Falls back to the decrement only for callers that predate `onRemove`. */
  const handleRemove = useCallback(
    () => (onRemove ? onRemove(addOn) : onDecrease(addOn)),
    [onRemove, onDecrease, addOn],
  );

  const titleId = `add-on-item-title-${addOn.id}`;
  const detailsId = `add-on-item-details-${addOn.id}`;
  const lockedId = `add-on-item-locked-${addOn.id}`;
  const noticeId = `add-on-item-notice-${addOn.id}`;

  const renderControl = (): JSX.Element | null => {
    /*
     * Nothing to draw: the notice below says why, and a control here invites a press that does
     * nothing. A line already added keeps its stepper, so a selection is never stranded.
     */
    if (isUnavailable && quantity === 0) {
      return null;
    }

    /*
     * The control is WITHHELD, not disabled: there is nothing this party's row can do until the
     * tenant clears the other party's selection, and a greyed stepper invites the press anyway.
     * `role="img"` with a label, not a bare icon — a bare <span> has no ARIA naming role.
     */
    if (isPartyLocked && quantity === 0) {
      return (
        <span
          id={lockedId}
          className="inline-flex size-8 shrink-0 items-center justify-center text-muted-foreground"
          data-testid={`add-on-item-locked-${addOn.id}`}
        >
          <Lock className="size-3.5" role="img" aria-label={intl.formatMessage({ id: 'add_on_party_locked' })} />
        </span>
      );
    }

    /*
     * The product's range admits exactly one quantity, so there is nothing to step. The label names
     * the ACTION rather than the state, which is what makes it correct on a locked party still
     * holding a line: there, removal is the only thing the control can do, and a control reading
     * "Added" would name the one thing it cannot.
     */
    if (isFixedQuantity) {
      return (
        <Button
          type="button"
          id={`add-on-item-pill-${addOn.id}`}
          variant={quantity > 0 ? 'secondary' : 'outline'}
          size="sm"
          /*
           * ⛔ `handleRemove`, never `handleDecrease`. A grandfathered line can sit ABOVE what its
           * range now admits, and stepping that down by one leaves a remainder while the control
           * says it removed the item.
           */
          onClick={quantity > 0 ? handleRemove : handleIncrease}
          /* Only the ADD face is held — at quantity > 0 this removes, which is always safe. */
          disabled={!canAdd && quantity === 0}
          /*
           * Self-reference plus the product title, so the name announces as "Remove · Coffee". A bare
           * "Remove" leaves a screen-reader user unable to tell which product the control acts on.
           */
          aria-labelledby={`add-on-item-pill-${addOn.id} ${titleId}`}
          data-testid={`add-on-item-pill-${addOn.id}`}
        >
          {intl.formatMessage({ id: quantity > 0 ? 'add_on_remove' : 'add_on_add' })}
        </Button>
      );
    }

    return (
      <div className="flex shrink-0 items-center gap-1" data-testid={`add-on-item-stepper-${addOn.id}`}>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="size-8 rounded-full"
          onClick={handleDecrease}
          disabled={quantity === 0}
          aria-label={intl.formatMessage({ id: 'add_on_remove' })}
          aria-describedby={titleId}
          data-testid={`add-on-item-decrease-${addOn.id}`}
        >
          <Minus className="size-4" aria-hidden="true" />
        </Button>
        {/*
          `aria-live` so stepping is audible: the count is the only thing that changes, and both
          buttons keep their own names, so nothing else would announce the result of the press.
        */}
        <span
          className="min-w-6 text-center text-sm font-medium tabular-nums"
          aria-live="polite"
          data-testid={`add-on-item-quantity-${addOn.id}`}
        >
          {quantity}
        </span>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="size-8 rounded-full"
          onClick={handleIncrease}
          disabled={!canAdd || isAtMax}
          aria-label={intl.formatMessage({ id: 'add_on_add' })}
          aria-describedby={titleId}
          data-testid={`add-on-item-increase-${addOn.id}`}
        >
          <Plus className="size-4" aria-hidden="true" />
        </Button>
      </div>
    );
  };

  /* The notice explains itself; the lock needs the hidden label. Never both -- one reason per row. */
  const describedBy = (): string | undefined => {
    if (isUnavailable) {
      return noticeId;
    }

    return isDimmed ? lockedId : undefined;
  };

  /*
   * Party lock only -- an out-of-window row is not dimmed, on either surface. A locked row has
   * nothing to say for itself, so the dimming carries the message; an out-of-window row has a
   * sentence under it and a price the tenant still has to read. The Service Request surface renders
   * that name at full contrast, and so does the ticket's screenshot.
   */
  const isDimmed = isPartyLocked && quantity === 0;

  return (
    <div className="py-3 first:pt-0 last:pb-0" data-testid={`add-on-item-${addOn.id}`}>
      <div className={`flex items-center gap-3 ${isDimmed ? 'opacity-60' : ''}`}>
        {/*
          ⚠️ The describedby sits on the BUTTON, not on the row. A bare div computes to
          `role="generic"`, which supports only global attributes — a description on one is not
          announced, and neither axe nor the a11y lint rules flag it.

          ⛔ `data-slot="button"` is the Preflight reset this app scopes to that attribute (app.css):
          without it the UA's grey bevelled button chrome survives underneath these utilities and the
          row renders as a grey slab. See the note in `add-ons-dialog.tsx`.
        */}
        <button
          type="button"
          data-slot="button"
          onClick={hasDetails ? handleToggle : undefined}
          disabled={!hasDetails}
          aria-expanded={hasDetails ? isExpanded : undefined}
          aria-controls={hasDetails ? detailsId : undefined}
          aria-describedby={describedBy()}
          className="flex min-w-0 flex-1 cursor-pointer items-center gap-1.5 text-left disabled:cursor-default"
          data-testid={`add-on-item-toggle-${addOn.id}`}
        >
          {/*
            ⛔ THE NAME WRAPS, THE SUBTITLE TRUNCATES, and the asymmetry is the point. Truncating
            both left an item nobody could finish reading: the disclosure is `disabled` without a
            description or an image, so a long name on a narrow dialog had no second place to be
            read -- not on the row, not in the details, and not on touch, where there is no title
            tooltip to fall back on. A tenant choosing and paying for something must be able to read
            what it is.

            The subtitle keeps `truncate` because it is a price line with a known short shape, and
            because the row needs one thing holding its height steady.
          */}
          <span className="min-w-0 flex-1">
            <span id={titleId} className="block text-sm font-medium text-foreground">
              {addOn.name}
            </span>
            {subtitle && <span className="block truncate text-sm text-muted-foreground">{subtitle}</span>}
          </span>
          {hasDetails && (
            <ChevronDown
              aria-hidden="true"
              className={`size-4 shrink-0 text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            />
          )}
        </button>
        {renderControl()}
      </div>
      {/*
        Why the row cannot be ordered, or -- where the booking still meets the window -- the lead time
        it needs. The icon is decorative; labelling it would announce the sentence twice.
      */}
      {notice && (
        <div
          id={noticeId}
          className="flex items-center gap-1.5 pt-2 text-xs text-muted-foreground"
          data-testid={`add-on-item-notice-${addOn.id}`}
        >
          <Clock className="size-3.5 shrink-0" aria-hidden="true" />
          {notice}
        </div>
      )}
      {isExpanded && hasDetails && (
        <div
          id={detailsId}
          className="pt-3 text-sm text-muted-foreground"
          data-testid={`add-on-item-details-${addOn.id}`}
        >
          {addOn.image_url && (
            <img src={addOn.image_url} alt={addOn.name} className="mb-2 max-h-40 w-full rounded-md object-cover" />
          )}
          {addOn.description}
        </div>
      )}
    </div>
  );
};
